/**
 * Hilfen für Threads und Forenbeiträge: Rechte, Listenabfrage, Zeitangaben.
 * Wird von threads/, forum/ und dms/ gemeinsam genutzt.
 */
import {
  hasPermission,
  Permissions,
  timestampFromSnowflake,
  type ChannelDto,
  type MessageDto,
} from '@lagerfeuer/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { messagesApi, threadsApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { useAppStore } from '../../store/app';

/** Höchstzahl Tags je Forenbeitrag (Backend: MAX_THREAD_TAGS). */
export const MAX_TAGS_PER_POST = 5;
export const THREAD_PAGE_SIZE = 25;

export type ThreadSort = 'activity' | 'created';

/** Antwort von POST /channels/:id/threads bzw. evtl. Listen mit additivem Feld `firstMessage`. */
export type ThreadWithFirst = ChannelDto & { firstMessage?: MessageDto };

export interface ThreadAccess {
  isOwner: boolean;
  isMod: boolean;
  canRename: boolean;
  canArchive: boolean;
  canLock: boolean;
  canDelete: boolean;
  canEditTags: boolean;
}

/**
 * Rechte am Thread laut Vertrag 3.4: Ersteller darf Name/Tags/Archiv ändern (nicht wenn gesperrt),
 * MANAGE_THREADS (im Elternkanal) darf alles inkl. Sperren und Löschen.
 */
export function threadAccess(thread: Pick<ChannelDto, 'ownerId' | 'thread'>, parentPerms: bigint, meId: string | undefined): ThreadAccess {
  const isMod = hasPermission(parentPerms, Permissions.MANAGE_THREADS);
  const isOwner = !!meId && thread.ownerId === meId;
  const ownerCan = isOwner && !thread.thread?.locked;
  return {
    isOwner,
    isMod,
    canRename: isMod || ownerCan,
    canArchive: isMod || ownerCan,
    canLock: isMod,
    canDelete: isMod,
    canEditTags: isMod || ownerCan,
  };
}

/** Antworten eines Threads: im Forum zählt die Startnachricht nicht mit. */
export function replyCount(thread: Pick<ChannelDto, 'thread'>, inForum: boolean): number {
  const n = thread.thread?.messageCount ?? 0;
  return inForum ? Math.max(0, n - 1) : n;
}

/** Zeitpunkt der letzten Aktivität (letzte Nachricht, sonst Erstellung) in ms. */
export function lastActivity(ch: Pick<ChannelDto, 'id' | 'lastMessageId'>): number {
  try {
    return timestampFromSnowflake(ch.lastMessageId ?? ch.id);
  } catch {
    return 0;
  }
}

const rtf = new Intl.RelativeTimeFormat('de', { numeric: 'auto' });
const STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/** „vor 5 Minuten", „gestern" … */
export function relativeTime(ms: number, now = Date.now()): string {
  const diff = Math.round((ms - now) / 1000);
  const abs = Math.abs(diff);
  for (const [unit, secs] of STEPS) {
    if (abs >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return 'gerade eben';
}

export function absoluteTime(ms: number): string {
  return new Date(ms).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Geladene Threads im Store ablegen, damit Echtzeit-Ereignisse (Zähler, Archiv) sie aktualisieren. */
export function rememberThreads(threads: ChannelDto[]): void {
  if (threads.length === 0) return;
  useAppStore.getState().update((d) => {
    for (const t of threads) {
      const { firstMessage: _f, ...plain } = t as ThreadWithFirst;
      d.channels[t.id] = plain;
    }
  });
}

/** Threads eines Kanals (aktiv/archiviert), seitenweise (Vertrag 3.4 GET /channels/:id/threads). */
export function useThreadList(channelId: string, opts: { archived: boolean; tagId?: string; sort?: ThreadSort; enabled?: boolean }) {
  const sort = opts.sort ?? 'activity';
  return useInfiniteQuery({
    queryKey: [...qk.threads(channelId, opts.archived, opts.tagId ?? ''), sort],
    enabled: opts.enabled ?? true,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await threadsApi.list(channelId, {
        archived: opts.archived,
        before: pageParam,
        limit: THREAD_PAGE_SIZE,
        tagId: opts.tagId || undefined,
        sort,
      });
      rememberThreads(res.threads);
      return res;
    },
    getNextPageParam: (last) => {
      if (!last.hasMore) return undefined;
      const t = last.threads[last.threads.length - 1];
      if (!t) return undefined;
      return sort === 'created' ? t.id : (t.lastMessageId ?? t.id);
    },
  });
}

/** Startnachricht eines Forenbeitrags (älteste Nachricht im Thread). */
export function useStarterMessage(threadId: string, preset?: MessageDto | null, enabled = true) {
  return useQuery({
    queryKey: ['forum-starter', threadId],
    queryFn: async () => (await messagesApi.list(threadId, { after: '0', limit: 1 }))[0] ?? null,
    enabled: enabled && !preset,
    initialData: preset ?? undefined,
    staleTime: 5 * 60_000,
  });
}

/** Thread aus dem Store, sonst die übergebene (geladene) Fassung. */
export function useLiveThread<T extends ChannelDto>(t: T): ChannelDto {
  return useAppStore((s) => s.channels[t.id]) ?? t;
}
